import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Crown, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Show ban message if a session was terminated by a ban
  React.useEffect(() => {
    const banMessage = localStorage.getItem('banMessage');
    if (banMessage) {
      setError(banMessage);
      localStorage.removeItem('banMessage');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);
    setLoading(false);

    if (result.success) {
      const promo = sessionStorage.getItem('pendingPromoCode');
      navigate(promo ? `/tiers?code=${encodeURIComponent(promo)}` : '/feed');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-black via-gray-900 to-amber-950/20 relative overflow-hidden">
      {/* Film grain texture */}
      <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]" style={{backgroundSize: '150px'}}></div>
      
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-md"
      >
        <Card className="bg-gray-900/95 border-2 border-amber-900/50 shadow-[0_0_30px_rgba(251,191,36,0.2)] backdrop-blur relative">
          {/* Classified stamp */}
          <motion.div 
            className="absolute -top-4 -right-4 bg-amber-700 text-black rounded-full w-20 h-20 flex items-center justify-center border-4 border-amber-800 transform -rotate-12 font-headline text-xs z-10"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: -12 }}
            transition={{ delay: 0.3, duration: 0.4, type: "spring", stiffness: 200 }}
          >
            <div className="text-center">
              <div>RETURN</div>
              <div className="text-xl">🔑</div>
              <div>ACCESS</div>
            </div>
          </motion.div>

          <CardHeader className="space-y-1 text-center border-b border-amber-900/30 pb-6">
            <motion.div 
              className="flex flex-col items-center mb-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <img 
                src="/logo.png" 
                alt="Deadline by Daria" 
                className="h-12 w-auto mb-2"
                style={{ 
                  mixBlendMode: 'screen',
                  filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, 0.7)) drop-shadow(0 0 20px rgba(220, 38, 38, 0.4))'
                }}
                data-testid="login-logo"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <CardTitle className="text-2xl font-headline text-amber-500 tracking-wider">ACCESS CASE FILES</CardTitle>
              <CardDescription className="text-gray-400 font-typewriter text-sm">
                Sign in to continue your investigation
              </CardDescription>
            </motion.div>
          </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div className="text-sm text-right">
              <Link to="/forgot-password" className="text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 border-t border-amber-900/30 pt-6">
            <motion.div 
              className="w-full"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider transition-all hover:shadow-[0_0_20px_rgba(251,191,36,0.4)]" disabled={loading}>
                {loading ? 'VERIFYING ACCESS...' : 'ENTER BUREAU'}
              </Button>
            </motion.div>
            <p className="text-sm text-center text-gray-400 font-typewriter">
              New detective?{' '}
              <Link to="/signup" className="text-amber-500 hover:text-amber-400 hover:underline transition-colors">
                Open new case file
              </Link>
            </p>
          </CardFooter>
        </form>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;